import { useMemo, useRef, useState } from 'react'
import { Alert, Collapse } from '@mantine/core'
import { IconAlertTriangle, IconChevronRight, IconChevronUp } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { BrandIdentity } from './BrandIdentity'
import { ContentCard, isButtonField, order } from './ContentCard'
import { RichTextV2 } from './RichTextV2'
import { V2FieldControl } from './V2FieldControl'
import { V2Collapse } from './V2Fields'
import { ClickoutControl } from '../generation/GenerationForm'
import { getAtPath } from '../../utils/configPaths'
import { matchesCondition } from '../../utils/conditions'
import { buildUiLayoutV2, fieldLabel, isWideField } from '../../utils/uiLayoutV2'
import { paletteSourceKey, stablePaletteSuggestions } from '../../utils/aiSuggestions'
import classes from './V2Form.module.css'

// Picking a Button Text colour must show in the ad: every preset draws the label with the
// automatic contrast colour, so the first explicit choice switches the label to that colour.
const COUPLED_UPDATES = {
  'data.colors.c4': { path: 'data.style.elements.cta.skin.text', when: 'auto', value: 'c4' },
}

// AD-252: card titles the sealed schemas still spell the old way.
const GROUP_TITLES = { layout: 'editorV2.form.layoutTitle' }

function V2Card({ title, collapsible, defaultOpen = true, tight = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  const header = collapsible ? (
    <button type="button" className={classes.header} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
      <h2 className={classes.title}>{title}</h2>
      <IconChevronUp size={24} stroke={1.6} className={classes.chevron} data-open={open ? 'true' : 'false'} aria-hidden="true" />
    </button>
  ) : (
    <div className={classes.header}><h2 className={classes.title}>{title}</h2></div>
  )
  return (
    <section className={classes.card} aria-label={title}>
      {header}
      <Collapse in={!collapsible || open} transitionDuration={160}>
        <div className={classes.divider} />
        <div className={classes.body} data-tight={tight ? 'true' : undefined}>{children}</div>
      </Collapse>
    </section>
  )
}

function itemFields(collection) {
  return (collection.itemSections || []).flatMap((section) => section.fields || [])
}

/** Splits "more" blocks into rows that each start with an option carousel or a tab block. */
function moreSections(blocks) {
  const sections = []
  for (const block of blocks) {
    const starts = block.kind === 'field' ? block.field.ui?.control === 'thumbnail-grid' : block.subgroup.control === 'tabs'
    if (starts || !sections.length) sections.push([block])
    else sections[sections.length - 1].push(block)
  }
  return sections
}

/**
 * The AD-209 editor cards for templates with `schema.ui.version === 2`. Campaign-level
 * fields (`placement: "modal"`) are edited in Settings, not here, except the clickout (AD-252).
 */
export function SchemaFormV2({
  schema,
  templateManifest,
  configuration,
  errors,
  updateValue,
  campaign,
  updateCampaign,
  addAsset,
  addRenditionPreview,
  assetUrls,
  assetSourceUrls,
  selectedItems,
  setSelectedItem,
  addCollectionItem,
  removeCollectionItem,
  reorderCollectionItem,
}) {
  const { t } = useTranslation()
  const layout = useMemo(() => buildUiLayoutV2(schema), [schema])
  const [moreOpen, setMoreOpen] = useState({})
  // One palette row for all colour fields, taken when the ad, template or generation run changes
  // and then held: rebuilding it from the live colours reshuffled it on every pick.
  const paletteRef = useRef({ key: null, colors: [] })
  const paletteKey = paletteSourceKey(configuration)
  if (paletteRef.current.key !== paletteKey) {
    paletteRef.current = { key: paletteKey, colors: stablePaletteSuggestions(configuration) }
  }
  const palette = paletteRef.current.colors

  const update = (path, value) => {
    updateValue(path, value)
    const coupled = COUPLED_UPDATES[path]
    if (coupled && getAtPath(configuration, coupled.path) === coupled.when) updateValue(coupled.path, coupled.value)
  }

  const isElementStyles = (block) => block.kind === 'subgroup' && block.subgroup.control === 'tabs'
  // AD-252: the font follows the colours.
  const isFont = (block) => block.kind === 'field' && block.field.ui?.control === 'font-select'
  const elementStyleFields = (layout ? layout.cards : [])
    .flatMap((group) => [...group.blocks, ...group.moreBlocks])
    .filter(isElementStyles)
    .flatMap((block) => block.fields)

  const shared = {
    configuration,
    templateManifest,
    updateValue: update,
    addAsset,
    addRenditionPreview,
    assetUrls,
    assetSourceUrls,
    elementStyleFields,
    renderStyleField: (field) => renderField(field),
  }

  const renderField = (field) => (
    <V2FieldControl
      field={field}
      path={field.path}
      value={getAtPath(configuration, field.path)}
      error={errors[field.path]}
      onChange={(next) => update(field.path, next)}
      configuration={configuration}
      templateManifest={templateManifest}
      addAsset={addAsset}
      addRenditionPreview={addRenditionPreview}
      assetUrls={assetUrls}
      assetSourceUrls={assetSourceUrls}
      palette={palette}
    />
  )

  // AD-253: the button is edited once, under the colours, and written to every slide. AD-252: it
  // has no link of its own; it leads where the Clickout does.
  const buttonCollection = (schema.collections || []).find((collection) => itemFields(collection).some(isButtonField))
  const buttonFields = buttonCollection
    ? itemFields(buttonCollection).filter((field) => isButtonField(field) && field.type !== 'url').sort((a, b) => order(a) - order(b))
    : []
  const slides = buttonCollection ? getAtPath(configuration, buttonCollection.path, []) || [] : []

  const renderButtonField = (field) => {
    const paths = slides.map((_, index) => `${buttonCollection.path}.${index}.${field.path}`)
    const props = {
      field,
      path: paths[0],
      value: getAtPath(slides[0], field.path),
      error: paths.map((path) => errors[path]).find(Boolean),
      onChange: (next) => paths.forEach((path) => updateValue(path, next)),
      configuration,
      item: slides[0],
    }
    return (
      <div key={`button:${field.path}`} className={classes.full}>
        {field.type === 'rich-text' ? <RichTextV2 {...props} /> : <V2FieldControl {...props} />}
      </div>
    )
  }

  const addItem = (collection) => {
    addCollectionItem(collection.id)
    if (collection !== buttonCollection || slides.length >= (collection.maxItems || Infinity)) return
    buttonFields.forEach((field) => updateValue(`${collection.path}.${slides.length}.${field.path}`, getAtPath(slides[0], field.path)))
  }

  if (!layout) return null

  // Errors on fields this page does not show (hidden controls, Campaign Setting) still need
  // a place: a form-level alert naming them.
  const modalGroups = new Set(layout.modal.map((group) => group.id))
  const campaignFields = (schema.sections || []).flatMap((section) => section.fields || [])
    .filter((field) => modalGroups.has(field.ui?.group))
  // AD-252: Clickout and its website sit under the brand name, as in the Ad Builder.
  const clickoutField = campaign ? campaignFields.find((field) => field.path === 'ad.clickoutUrl') : null
  const modalFields = campaignFields.filter((field) => field !== clickoutField)
  const unseen = [...layout.hidden, ...modalFields].filter((field) => errors[field.path])

  const clickout = clickoutField && (
    <div key="clickout" className={`${classes.full} ${classes.clickout}`}>
      <ClickoutControl value={campaign.clickoutMode} onChange={(clickoutMode) => updateCampaign({ clickoutMode })} />
      {campaign.clickoutMode === 'own-website' && renderField(clickoutField)}
    </div>
  )

  const renderBlocks = (group, blocks) => {
    const panel = blocks.find((block) => block.kind === 'subgroup' && block.subgroup.control === 'panel')
    const colours = blocks.filter((block) => block.kind === 'field' && block.field.type === 'color'
      && matchesCondition(block.field.visibleWhen, configuration))
    return blocks.map((block) => {
      if (block.kind === 'field') {
        const { field } = block
        if (!matchesCondition(field.visibleWhen, configuration)) return null
        if (colours.length > 1 && colours.includes(block)) {
          if (block !== colours[0]) return null
          return (
            <div key="colours" className={`${classes.full} ${classes.box}`}>
              <span className={classes.label}>{t('editorV2.form.colors')}</span>
              <div className={classes.grid}>
                {colours.map((colour) => <div key={colour.key} className={classes.cell}>{renderField(colour.field)}</div>)}
              </div>
            </div>
          )
        }
        if (field.ui?.control === 'brand-identity') {
          return (
            <div key={block.key} className={classes.full}>
              <BrandIdentity modeField={field} fields={panel ? panel.fields : []} shared={shared} errors={errors} />
            </div>
          )
        }
        return (
          <div key={block.key} className={isWideField(field) ? classes.full : classes.cell}>
            {renderField(field)}
          </div>
        )
      }
      const { subgroup, fields } = block
      if (subgroup.control === 'panel') return null
      if (subgroup.control === 'collapse') {
        return (
          <V2Collapse key={block.key} title={subgroup.title} defaultOpen={subgroup.defaultOpen === true}>
            {fields.map((field) => (
              <div key={field.path} className={isWideField(field) && field.ui?.width !== 'half' ? classes.full : classes.cell}>
                {renderField(field)}
              </div>
            ))}
          </V2Collapse>
        )
      }
      return fields.map((field) => (
        <div key={field.path} className={isWideField(field) ? classes.full : classes.cell}>{renderField(field)}</div>
      ))
    })
  }

  return (
    <>
      {unseen.length > 0 && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} className={classes.formAlert}>
          {unseen.map((field) => (
            <div key={field.path}>
              <strong>{fieldLabel(field)}</strong>
              {modalGroups.has(field.ui?.group) ? ` (${t('editorV2.form.inSettings')})` : ''}: {errors[field.path]}
            </div>
          ))}
        </Alert>
      )}

      {layout.cards.map((group) => {
        if (group.sections.length && !group.fields.length) {
          return group.sections.map((section) => {
            const collection = (schema.collections || []).find((entry) => entry.id === section.collection)
            if (!collection) return null
            return (
              <ContentCard
                key={section.id}
                title={group.title}
                section={section}
                collection={collection}
                shared={shared}
                errors={errors}
                selectedIndex={selectedItems?.[collection.id] || 0}
                onSelect={(index) => setSelectedItem(collection.id, index)}
                onAdd={() => addItem(collection)}
                onRemove={(index) => removeCollectionItem(collection.id, index)}
                onReorder={(from, to) => reorderCollectionItem(collection.id, from, to)}
              />
            )
          })
        }

        const open = Boolean(moreOpen[group.id])
        const blocks = group.blocks.filter((block) => !isElementStyles(block))
        const ordered = [...blocks.filter((block) => !isFont(block)), ...blocks.filter(isFont)]
        const named = group.id === 'brand' && clickout
          ? ordered.findIndex((block) => block.kind === 'field' && block.field.path === 'data.brand.name') + 1
          : 0
        const moreBlocks = group.moreBlocks.filter((block) => !isElementStyles(block))
        const sections = moreSections(moreBlocks)
        return (
          <V2Card
            key={group.id}
            title={GROUP_TITLES[group.id] ? t(GROUP_TITLES[group.id]) : group.title}
            collapsible={group.collapsible !== false}
            defaultOpen={group.defaultOpen !== false}
            tight={moreBlocks.length > 0}
          >
            <div className={classes.grid}>
              {renderBlocks(group, ordered.slice(0, named))}
              {group.id === 'brand' && clickout}
              {renderBlocks(group, ordered.slice(named))}
              {group.id === 'brand' && slides.length > 0 && buttonFields.map(renderButtonField)}
            </div>
            {open && sections.map((section, index) => (
              <div key={section[0].key} style={{ display: 'contents' }}>
                {index >= 2 && <div className={classes.rule} />}
                <div className={classes.grid}>{renderBlocks(group, section)}</div>
              </div>
            ))}
            {group.more && moreBlocks.length > 0 && (
              <div className={classes.more}>
                <button
                  type="button"
                  className={classes.moreButton}
                  aria-expanded={open}
                  onClick={() => setMoreOpen((current) => ({ ...current, [group.id]: !open }))}
                >
                  {open ? (group.more.hideLabel || t('editorV2.form.showLess')) : (group.more.showLabel || t('editorV2.form.showMore'))}
                  {open ? <IconChevronUp size={16} stroke={1.8} /> : <IconChevronRight size={16} stroke={1.8} />}
                </button>
              </div>
            )}
          </V2Card>
        )
      })}
    </>
  )
}
